import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchControllerStatuses } from '@/features/controllerStatus/api'
import LoadingState from '@/components/common/LoadingState'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'

/**
 * Ports the "Controller Status" tab of includes/controller-status.html —
 * the fleet-wide bus-state view. The other tabs on that legacy page
 * (Alarms, Deployment/firmware, Download, Vision Portal unavailability) are
 * separate, more specialized features not covered by this page yet.
 */
export default function ControllerStatusPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['controller-status'],
    queryFn: fetchControllerStatuses,
  })
  const [expandedId, setExpandedId] = useState<string | null>(null)

  if (isLoading) return <LoadingState label="Loading controller status…" />
  if (isError) {
    return (
      <ErrorState message={error instanceof Error ? error.message : 'Failed to load.'} onRetry={() => refetch()} />
    )
  }
  if (!data || data.length === 0) return <EmptyState message="No controllers found." />

  return (
    // Same shrink-0-header / flex-1-scroller split as DashboardPage: fills
    // AppLayout's <main> exactly (h-full) so the controller list is the
    // ONLY thing that scrolls, with the title staying pinned.
    <div className="flex h-full flex-col">
      <h1 className="mb-4 shrink-0 text-xl font-semibold text-text-primary">Controller Status</h1>
      <div className="min-h-0 flex-1 divide-y divide-border overflow-y-auto rounded border border-border bg-card">
        {data.map((c) => (
          <div key={c.controller_id}>
            <button
              type="button"
              onClick={() => setExpandedId(expandedId === c.controller_id ? null : c.controller_id)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-bg-secondary"
            >
              <span className="text-sm text-text-primary">{c.controller_name || c.controller_id}</span>
              <span className="text-xs text-text-secondary">{c.status.timestamp}</span>
            </button>
            {expandedId === c.controller_id && (
              <div className="bg-bg-secondary px-4 py-3 text-sm text-text-primary">
                Current bus state: <strong>{c.status.CURRENT_BUS_STATE.replace(/_/g, ' ')}</strong>
                {c.status.BUS_IN_TRANSITION && (
                  <span className="ml-2 text-xs text-text-secondary">
                    (transitioning{c.status.CURRENT_BUS_TRANSITION ? ` to ${c.status.CURRENT_BUS_TRANSITION}` : ''})
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
